/** Build Sph/Cyl dropdown options (Plan, -20.00…+10.00). */
export function buildOpticalSelectOptions() {
  const options = [{ value: '0', label: 'Plan' }];
  for (let step = 1; step <= 80; step += 1) {
    const abs = (step * 0.25).toFixed(2);
    options.push({ value: `-${abs}`, label: `-${abs}` });
  }
  for (let step = 1; step <= 40; step += 1) {
    const abs = (step * 0.25).toFixed(2);
    options.push({ value: `+${abs}`, label: `+${abs}` });
  }
  return options;
}

/** @deprecated use buildOpticalSelectOptions — kept for imports */
export const OPTICAL_SELECT_OPTIONS = buildOpticalSelectOptions();

/** True when an optical field has a meaningful value (including 0 / Plan). */
export function hasOpticalValue(value) {
  return value !== null && value !== undefined && value !== '';
}

/** Format sph/cyl for ModernOpticalSelect options (e.g. -2.5 → "-2.50", 0 → "0"). */
export function normalizeOpticalSelectValue(value) {
  if (!hasOpticalValue(value)) return '';
  const raw = String(value).trim();
  const num = parseFloat(raw.replace(/^\+/, ''));
  if (Number.isNaN(num)) return raw;
  if (num === 0) return '0';
  const abs = Math.abs(num).toFixed(2);
  return num > 0 ? `+${abs}` : `-${abs}`;
}

/** Build lens correction form state from an order item. */
export function lensFormFromItem(item) {
  if (!item) {
    return { sph: '', cyl: '', axe: '', addition: '' };
  }
  return {
    sph: normalizeOpticalSelectValue(item.sph),
    cyl: normalizeOpticalSelectValue(item.cyl),
    axe: hasOpticalValue(item.axe) ? String(item.axe) : '',
    addition: hasOpticalValue(item.addition) ? String(item.addition) : '',
  };
}

/** Copy order-level prescription onto verre / lentilles line items. */
export function applyOrderOpticalToLensItems(items, order) {
  if (!Array.isArray(items) || !order) return items;

  for (const itemType of ['verre', 'lentilles']) {
    const typeItems = items.filter((item) => item.item_type === itemType);
    const leftLens = typeItems.find((item) => item.side === 'left');
    const rightLens = typeItems.find((item) => item.side === 'right');

    if (leftLens) {
      leftLens.sph = order.left_sph;
      leftLens.cyl = order.left_cyl;
      leftLens.axe = order.left_axis;
      leftLens.addition = order.left_addition;
    }

    if (rightLens) {
      rightLens.sph = order.right_sph;
      rightLens.cyl = order.right_cyl;
      rightLens.axe = order.right_axis;
      rightLens.addition = order.right_addition;
    }

    const legacy = typeItems.find((item) => item.side === 'none' || !item.side);
    if (legacy && !leftLens && !rightLens) {
      legacy._opticalLeft = {
        sph: order.left_sph,
        cyl: order.left_cyl,
        axe: order.left_axis,
        addition: order.left_addition,
      };
      legacy._opticalRight = {
        sph: order.right_sph,
        cyl: order.right_cyl,
        axe: order.right_axis,
        addition: order.right_addition,
      };
    }
  }

  return items;
}

/** Prefer verre items for optical payload; fall back to lentilles. */
export function getOpticalSourceItems(items) {
  const verres = (items || []).filter((item) => item.item_type === 'verre');
  if (verres.length) return verres;
  return (items || []).filter((item) => item.item_type === 'lentilles');
}

/** Resolve left/right lens rows for order save. */
export function getOpticalEyesFromItems(items) {
  const source = getOpticalSourceItems(items);
  return {
    left: source.find((item) => item.side === 'left') || null,
    right: source.find((item) => item.side === 'right') || null,
  };
}

/** Hydrate modal state when editing verre or lentilles. */
export function resolveOpticalEditState(items, itemType, currentItem) {
  const opticalItems = (items || []).filter((item) => item.item_type === itemType);
  const leftItem = opticalItems.find((item) => item.side === 'left');
  const rightItem = opticalItems.find((item) => item.side === 'right');
  const sameProductBoth =
    leftItem &&
    rightItem &&
    String(leftItem.product_id) === String(rightItem.product_id);

  if (leftItem && rightItem && leftItem.product_id !== rightItem.product_id) {
    return {
      lensSelectionType: 'both_different',
      leftLensForm: lensFormFromItem(leftItem),
      rightLensForm: lensFormFromItem(rightItem),
      leftLensProduct: { product_id: String(leftItem.product_id), price: leftItem.price ?? '' },
      rightLensProduct: { product_id: String(rightItem.product_id), price: rightItem.price ?? '' },
    };
  }

  if (sameProductBoth && (currentItem.side === 'left' || currentItem.side === 'right')) {
    return {
      lensSelectionType: 'both_same',
      leftLensForm: lensFormFromItem(leftItem),
      rightLensForm: lensFormFromItem(rightItem),
    };
  }

  if (currentItem.side === 'left') {
    return {
      lensSelectionType: 'left_only',
      leftLensForm: lensFormFromItem(currentItem),
      rightLensForm: { sph: '', cyl: '', axe: '', addition: '' },
    };
  }

  if (currentItem.side === 'right') {
    return {
      lensSelectionType: 'right_only',
      rightLensForm: lensFormFromItem(currentItem),
      leftLensForm: { sph: '', cyl: '', axe: '', addition: '' },
    };
  }

  if ((currentItem.side === 'none' || !currentItem.side) && currentItem._opticalLeft) {
    return {
      lensSelectionType: 'both_same',
      leftLensForm: lensFormFromItem(currentItem._opticalLeft),
      rightLensForm: lensFormFromItem(currentItem._opticalRight),
    };
  }

  return {
    lensSelectionType: '',
    leftLensForm: lensFormFromItem(leftItem),
    rightLensForm: lensFormFromItem(rightItem),
  };
}

export function isOpticalItemType(type) {
  return type === 'verre' || type === 'lentilles';
}

/** Wide modal + 2-column layout once a lens product is chosen (or when editing). */
export function isOpticalModalExpanded(modalType, { lensSelectionType, modalForm, leftLensProduct, rightLensProduct, isEdit = false }) {
  if (!isOpticalItemType(modalType)) return false;
  if (isEdit) return true;
  if (lensSelectionType === 'both_different') {
    return Boolean(leftLensProduct?.product_id || rightLensProduct?.product_id);
  }
  return Boolean(modalForm?.product_id);
}

/** Preserve 0 for numeric form fields (PD, hauteurs). */
export function numericFormValue(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

/** Compact sph/cyl label e.g. "-9.00 -4.50" or "+12 +8". */
export function formatOpticalCorrectionSummary(source) {
  if (!source) return '';
  const parts = [];
  if (hasOpticalValue(source.sph)) parts.push(String(source.sph));
  if (hasOpticalValue(source.cyl)) parts.push(String(source.cyl));
  return parts.join(' ');
}

/** Parse sph/cyl/axe/addition for API payload (handles "+2.50", commas). */
export function parseOpticalNumber(value) {
  if (!hasOpticalValue(value)) return null;
  const raw = String(value).trim().replace(',', '.');
  const n = parseFloat(raw.replace(/^\+/, ''));
  return Number.isFinite(n) ? n : null;
}
